import {datadogLogs} from '@datadog/browser-logs'
import { queueTrackingEvent } from '@library/tracking-integration'
import {client as clientRoutes} from 'config/routes'
import logger from 'utils/logger'
import {seActions} from 'middlewares/tracking/snowplow/pauseSubscription/seActions'
import * as windowUtils from 'utils/window'
import {fetchOrderSkipContent, fetchSubscriptionPauseContent} from 'apis/onScreenRecovery'
import {actionTypes} from './actionTypes'
import {orderCancel, projectedOrderCancel} from './order'
import {redirect} from './redirect'
import subPauseActions from './subscriptionPause'
import userActions from './user'
import statusActions from './status'

import {trackOrderCancelled} from './loggingmanager'

const SERVER_ERROR_500 = 500

const getDiscountReminderActionType = ({ orderType, hasDiscount }) => {
  if (orderType === 'pending') {
    return hasDiscount ? 'Order Cancel' : 'Order Cancelled'
  }

  return hasDiscount ? 'Order Skip' : 'Order Skipped'
}

const buildDiscountReminderProperties = ({
  orderId,
  deliveryDayId,
  orderType,
  data = {},
  hasDiscount = false,
}) => {
  const properties = {
    actionType: getDiscountReminderActionType({ orderType, hasDiscount }),
    delivery_day_id: deliveryDayId,
    order_state: orderType,
    cms_variation: data.variation || 'default',
    recovery_reasons: [
      data.valueProposition || null,
      data.offer || null,
    ],
    modal_shown: hasDiscount,
  }

  if (hasDiscount || orderType === 'pending') {
    properties.order_id = orderId
  }

  return properties
}

export const generateModalTrackingData = ({
  modalVisibility,
  orderType,
  modalType,
  orderId,
  deliveryDayId,
  data,
}) => {
  if (modalType === 'subscription') {
    return modalVisibility
      ? null
      : { actionType: seActions.SUBSCRIPTION_KEPT_ACTIVE }
  }

  if (!modalVisibility) {
    return null
  }

  const actionType = (orderType === 'pending') ? 'Order Cancel' : 'Order Skip'

  return {
    actionType,
    order_id: orderId,
    delivery_day_id: deliveryDayId,
    order_state: orderType,
    cms_variation: data.variation || 'default',
    recovery_reasons: [
      data.valueProposition,
      data.offer,
    ],
    modal_shown: true,
  }
}

export const trackViewDiscountReminder = (properties = {}) => () => {
  queueTrackingEvent({
    action: seActions.VIEW_PAUSE_DISCOUNT_REMINDER_OFFER_SCREEN,
    properties: {
      modal_shown: true,
      ...properties,
    },
  })
}

export const modalVisibilityChange = ({
  orderId,
  deliveryDayId,
  orderType,
  modalType,
  data = {},
  modalVisibility = true,
}) => (
  (dispatch) => {
    const trackingData = generateModalTrackingData({
      modalVisibility,
      orderType,
      modalType,
      orderId,
      deliveryDayId,
      data,
    })

    dispatch({
      type: actionTypes.ORDER_SKIP_RECOVERY_MODAL_VISIBILITY_CHANGE,
      modalVisibility,
      orderId,
      deliveryDayId,
      modalType,
      title: data.title,
      offer: data.offer,
      ...(modalVisibility ? { cmsVariation: data.variation || 'default' } : {}),
      orderType,
      callToActions: data.callToActions,
      valueProposition: data.valueProposition,
      trackingData,
    })
  }
)

export const keepOrder = () => (
  async (dispatch, getState) => {
    const { onScreenRecovery } = getState()
    const valueProposition = onScreenRecovery.getIn(['orderSkip', 'valueProposition'])
    const orderId = onScreenRecovery.getIn(['orderSkip', 'orderId'])
    const deliveryDayId = onScreenRecovery.getIn(['orderSkip', 'deliveryDayId'])
    const orderType = onScreenRecovery.getIn(['orderSkip', 'orderType'])
    const offer = onScreenRecovery.getIn(['orderSkip', 'offer'])
    const promoCode = offer ? offer.get('promoCode') : null

    if (promoCode) {
      dispatch(statusActions.error(actionTypes.PROMO_APPLY, ''))
      await dispatch(userActions.userPromoApplyCode(promoCode))

      const errorInApplyPromo = getState().error && getState().error.get(actionTypes.PROMO_APPLY)

      if (errorInApplyPromo) {
        dispatch({
          type: actionTypes.TRACKING,
          trackingData: {
            actionType: 'Failed in applying OSR promo code',
            osrDiscount: promoCode,
          },
        })
        datadogLogs.logger.error('OrderSkipOSR: promo code not applied', {
          promoCode,
          deliveryDayId,
          errorInApplyPromo,
          keptOrderId: orderId,
        })

        return
      }

      datadogLogs.logger.info('OrderSkipOSR: order recovered', { keptOrderId: orderId })
    }

    const trackedOffer = offer ? offer.toJS() : null

    dispatch({
      type: actionTypes.ORDER_SKIP_RECOVERY_TRIGGERED,
      triggered: false,
      trackingData: {
        actionType: 'Order Kept',
        order_id: orderId,
        delivery_day_id: deliveryDayId,
        order_state: orderType,
        recovery_reasons: [
          valueProposition,
          trackedOffer,
        ],
      },
    })
  }
)

export const keepSubscription = () => (
  async (dispatch, getState) => {
    const userId = getState().user.get('id')
    const promoCode = getState().onScreenRecovery.getIn(['subscriptionPause', 'promoCode'])
    if (promoCode) {
      await dispatch(userActions.userPromoApplyCode(promoCode))
      const errorInApplyPromo = getState().error && getState().error.get(actionTypes.PROMO_APPLY)

      if (errorInApplyPromo) {
        dispatch({
          type: actionTypes.TRACKING,
          trackingData: {
            actionType: 'Failed in applying OSR promo code',
            osrDiscount: promoCode,
          },
        })

        /*
          Early return to prevent modal close
          Because a user failed to apply OSR promo code.
          // TODO show error message or notification
        */
        return
      }
    }
    dispatch({
      type: actionTypes.SUB_PAUSE_RECOVERY_TRIGGERED,
      triggered: false,
      trackingData: {
        actionType: seActions.SUBSCRIPTION_KEPT_ACTIVE,
        customerId: userId,
        osrDiscount: promoCode,
      },
    })
  }
)

export const cancelPendingOrder = (variation = 'default') => (
  async (dispatch, getState) => {
    const orderId = getState().onScreenRecovery.getIn(['orderSkip','orderId'])
    const deliveryDayId = getState().onScreenRecovery.getIn(['orderSkip','deliveryDayId'])
    const forceRefresh = getState().onScreenRecovery.getIn(['orderSkip','forceRefresh'])
    const orderDate = getState().onScreenRecovery.getIn(['orderSkip','orderDate']).split(' ')[0]

    try {
      await dispatch(orderCancel(orderId, deliveryDayId, variation))
      datadogLogs.logger.info('OrderSkipOSR: order cancelled', { keptOrderId: orderId })
      dispatch(trackOrderCancelled({ orderId, deliveryDate: orderDate }))
    } catch (err) {
      logger.error(err)
    } finally {
      if (forceRefresh) {
        dispatch(redirect('/my-deliveries'))
      }

      modalVisibilityChange({ modalVisibility: false })(dispatch)
    }
  }
)

export const cancelProjectedOrder = (variation = 'default') => (
  async (dispatch, getState) => {
    const deliveryDayId = getState().onScreenRecovery.getIn(['orderSkip','deliveryDayId'])
    const forceRefresh = getState().onScreenRecovery.getIn(['orderSkip', 'forceRefresh'])

    try {
      await dispatch(projectedOrderCancel(deliveryDayId, deliveryDayId, variation))
      datadogLogs.logger.info('OrderSkipOSR: projected delivery cancelled', { deliveryDayId })
    } catch (err) {
      logger.error(err)
    } finally {
      if (forceRefresh) {
        dispatch(redirect('/my-deliveries'))
      }
      modalVisibilityChange({ modalVisibility: false })(dispatch)
    }
  }
)

export const getSkipRecoveryContent = () => (
  async (dispatch, getState) => {
    const orderDate = getState().onScreenRecovery.getIn(['orderSkip', 'orderDate'])
    const orderId = getState().onScreenRecovery.getIn(['orderSkip', 'orderId'])
    const deliveryDayId = getState().onScreenRecovery.getIn(['orderSkip', 'deliveryDayId'])
    const orderType = getState().onScreenRecovery.getIn(['orderSkip', 'orderType'])
    const modalType = 'order'
    const accessToken = getState().auth.get('accessToken')
    dispatch(statusActions.pending(actionTypes.ORDER_SKIP_RECOVERY_TRIGGERED, true))
    dispatch(statusActions.error(actionTypes.ORDER_SKIP_RECOVERY_TRIGGERED, null))

    try {
      const { data } = await fetchOrderSkipContent(accessToken, orderId, orderDate)
      if (data.intervene) {
        dispatch(modalVisibilityChange({
          orderId,
          deliveryDayId,
          orderType,
          modalType,
          data,
        }))
      } else if (orderType === 'pending') {
        trackViewDiscountReminder(buildDiscountReminderProperties({
          orderId,
          deliveryDayId,
          orderType,
          data,
        }))(dispatch)
        await cancelPendingOrder(orderId, deliveryDayId)(dispatch, getState)
      } else {
        trackViewDiscountReminder(buildDiscountReminderProperties({
          orderId,
          deliveryDayId,
          orderType,
          data,
        }))(dispatch)
        await cancelProjectedOrder(deliveryDayId)(dispatch, getState)
      }
    } catch (err) {
      dispatch(modalVisibilityChange({
        orderId,
        deliveryDayId,
        orderType,
        modalType,
      }))

      logger.error(err)
      dispatch(statusActions.error(actionTypes.ORDER_SKIP_RECOVERY_TRIGGERED, err))
    } finally {
      dispatch(statusActions.pending(actionTypes.ORDER_SKIP_RECOVERY_TRIGGERED, false))
    }
  }
)

export const getPauseRecoveryContent = (enableOffer = true) => (
  async (dispatch, getState) => {
    const accessToken = getState().auth.get('accessToken')
    const modalType = 'subscription'

    dispatch(statusActions.pending(actionTypes.SUB_PAUSE_RECOVERY_TRIGGERED, true))
    dispatch(statusActions.error(actionTypes.SUB_PAUSE_RECOVERY_TRIGGERED, null))

    let isIntervened = true

    try {
      const { data } = await fetchSubscriptionPauseContent(accessToken, enableOffer)

      isIntervened = data.intervene

      if (data.intervene) {
        dispatch({
          type: actionTypes.SUB_PAUSE_RECOVERY_TRIGGERED,
          triggered: true,
          modalVisibility: true,
          modalType,
          title: data.title,
          offers: data.offers,
          promoCode: data.promoCode,
          callToActions: data.callToActions,
        })

        const onScreenRecoveryState = getState().onScreenRecovery
        const promoCode = onScreenRecoveryState.getIn(['subscriptionPause','promoCode'])

        const hasPendingPromo = !!promoCode

        dispatch({
          type: actionTypes.TRACKING,
          trackingData: {
            actionType: seActions.SUBSCRIPTION_PAUSE_ATTEMPT,
            discountOffered: promoCode,
            hasPendingPromo,
            hasPendingPromoWithSubCondition: null,
            promoCode,
          },
        })
      } else {
        dispatch(subPauseActions.subscriptionDeactivate())
      }
    } catch (err) {
      logger.error(err)
      // subpauseosr might return 5xx error, do not block the end user, let it deactivate its subscription
      if (err.code >= SERVER_ERROR_500 && isIntervened) {
        await dispatch(pauseSubscription())

        return
      }
      dispatch(statusActions.error(actionTypes.SUB_PAUSE_RECOVERY_TRIGGERED, err))
    } finally {
      dispatch(statusActions.pending(actionTypes.SUB_PAUSE_RECOVERY_TRIGGERED, false))
    }
  }
)

export const cancelOrder = () => (
  async (dispatch, getState) => {
    const orderType = getState().onScreenRecovery.getIn(['orderSkip', 'orderType'])
    if (orderType === 'pending') {
      cancelPendingOrder()(dispatch, getState)
    } else {
      cancelProjectedOrder()(dispatch, getState)
    }
  }
)

export const pauseSubscription = () => (
  async (dispatch, getState) => {
    const { user, onScreenRecovery } = getState()
    const pauseReason = onScreenRecovery.getIn(['subscriptionPause', 'pauseReason'], '')

    await dispatch(subPauseActions.subscriptionDeactivate(pauseReason))

    const userId = user.get('id')
    const promoCode = onScreenRecovery.getIn(['subscriptionPause', 'promoCode'], null)
    const subscriptionPageURL = clientRoutes.mySubscription

    dispatch({
      type: actionTypes.SUB_PAUSE_RECOVERY_TRIGGERED,
      triggered: false,
      trackingData: {
        actionType: seActions.SUBSCRIPTION_PAUSED,
        customerId: userId,
        osrDiscount: promoCode,
      },
    })

    windowUtils.redirect(subscriptionPageURL)
  }
)

export const orderCancelStart = (
  orderId,
  deliveryDayId,
  orderDate,
  orderType
) => (
  (dispatch) => {
    dispatch({
      type: actionTypes.ORDER_SKIP_RECOVERY_TRIGGERED,
      triggered: true,
      orderId,
      deliveryDayId,
      orderDate,
      modalType: 'order',
      orderType,
    })
  }
)

export const startOnScreenRecoverySubscriptionFlow = () => (
  (dispatch) => {
    dispatch({
      type: actionTypes.SUB_PAUSE_RECOVERY_TRIGGERED,
      triggered: true,
    })
  }
)

export const selectPauseReason = (pauseReason) => (
  (dispatch) => {
    dispatch({
      type: actionTypes.SUBSCRIPTION_PAUSE_OSR_REASON_SELECTED,
      pauseReason,
    })
  }
)
